import axios from "axios";
import datadogLogger from "./DatadogLogger";
import type { ErrorType } from "./interfaces";

// WeakMap avoids mutating the AxiosRequestConfig type and prevents memory leaks.
const requestTimings = new WeakMap<object, number>();

/**
 * Strips dynamic path segments (UUIDs and numeric IDs) to reduce cardinality
 * in Datadog metrics.
 *
 * Examples:
 *   /api/tickets/123        → /api/tickets/{id}
 *   /api/users/abc-123-def  → /api/users/{id}
 */
export function normalizeEndpoint(url?: string): string {
	if (!url) return "unknown";
	const path = url.split("?")[0]; // drop query string
	return path
		.replace(
			/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
			"{id}",
		)
		.replace(/\/\d{3,}/g, "/{id}");
}

export function classifyError(error: {
	response?: { status: number };
}): ErrorType {
	if (!error.response) return "network_error";
	const status = error.response.status as number;
	if (status === 401 || status === 403) return "auth_error";
	if (status >= 400 && status < 500) return "client_error";
	if (status >= 500) return "server_error";
	return "unknown";
}

/**
 * Call once at app startup (e.g. index.tsx) after datadogLogger.init().
 * Attaches global axios interceptors that log every API call outcome.
 */
export function setupAxiosInterceptors(): void {
	axios.interceptors.request.use((config) => {
		requestTimings.set(config, Date.now());
		return config;
	});

	axios.interceptors.response.use(
		(response) => {
			const startTime = requestTimings.get(response.config) ?? Date.now();
			const duration_ms = Date.now() - startTime;

			datadogLogger.logApiCall({
				endpoint: normalizeEndpoint(response.config.url),
				method: (response.config.method ?? "UNKNOWN").toUpperCase(),
				status: response.status,
				duration_ms,
				success: true,
			});

			return response;
		},
		(error) => {
			const config = error.config ?? {};
			const startTime = requestTimings.get(config) ?? Date.now();
			const duration_ms = Date.now() - startTime;

			datadogLogger.logApiCall({
				endpoint: normalizeEndpoint(config.url),
				method: (config.method ?? "UNKNOWN").toUpperCase(),
				status: error.response?.status ?? 0,
				duration_ms,
				success: false,
				error_type: classifyError(error),
			});

			return Promise.reject(error);
		},
	);
}
